/**
 * Chrome Extension Communication Bridge
 * Handles communication between the React app and the MercadoPro-Extension-Investigacion extension.
 * Protocol: MP_EXT_READY (ready signal), MP_EXT_SEARCH (request), MP_EXT_RESULT (response).
 */

/**
 * Check if the MercadoPro extension is installed.
 * The extension sends MP_EXT_READY on load and again at 800ms.
 */
export function checkExtensionInstalled() {
  return new Promise((resolve) => {
    let resolved = false;

    const handleMessage = (event) => {
      if (resolved) return;
      if (
        event.data &&
        event.data.source === 'mp-ext' &&
        event.data.type === 'MP_EXT_READY'
      ) {
        resolved = true;
        window.removeEventListener('message', handleMessage);
        resolve({ installed: true, version: event.data.version || '1.0.0' });
      }
    };

    window.addEventListener('message', handleMessage);

    window.postMessage({ type: 'MP_EXT_PING' }, '*');

    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        window.removeEventListener('message', handleMessage);
        resolve({ installed: false });
      }
    }, 2000);
  });
}

/**
 * True when extension version `version` is >= `minVersion` ("1.0.11" style).
 */
export function isExtensionVersionAtLeast(version, minVersion) {
  const a = String(version || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const b = String(minVersion || '0').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return true;
}

/**
 * Ask the extension to check the Chrome Web Store for a newer version now.
 * Resolves with { status: 'update_available' | 'checking' | 'no_update' | 'throttled' | 'error', ... }
 * or { status: 'unsupported' } when the installed version predates this
 * message (no reply) — those users must update by hand.
 */
export function requestExtensionUpdate() {
  return new Promise((resolve) => {
    const messageId = `upd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const timeout = setTimeout(() => {
      window.removeEventListener('message', handleResponse);
      resolve({ status: 'unsupported' });
    }, 15000);

    const handleResponse = (event) => {
      if (
        event.data &&
        event.data.source === 'mp-ext' &&
        event.data.type === 'MP_EXT_UPDATE_RESULT' &&
        event.data.messageId === messageId
      ) {
        clearTimeout(timeout);
        window.removeEventListener('message', handleResponse);
        resolve(event.data.error ? { status: 'error', error: event.data.error } : event.data.response);
      }
    };

    window.addEventListener('message', handleResponse);
    window.postMessage({ type: 'MP_EXT_UPDATE_CHECK', messageId }, '*');
  });
}

/**
 * Send a search request to the extension.
 * params must include: { searchTerms: string[], siteId, backendUrl, authToken }
 * The extension opens tabs for each term sequentially, so we use a 3-minute timeout.
 */
export function sendToExtension(action, params = {}) {
  return new Promise((resolve, reject) => {
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    // 3 min: extension opens up to 4 tabs sequentially + visit queries per item
    const timeoutMs = action === 'searchProducts' ? 180000 : 30000;

    const timeout = setTimeout(() => {
      window.removeEventListener('message', handleResponse);
      reject(new Error(`Extension timeout (${timeoutMs / 1000}s)`));
    }, timeoutMs);

    const handleResponse = (event) => {
      if (
        event.data &&
        event.data.source === 'mp-ext' &&
        event.data.type === 'MP_EXT_RESULT' &&
        event.data.messageId === messageId
      ) {
        clearTimeout(timeout);
        window.removeEventListener('message', handleResponse);

        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.response);
        }
      }
    };

    window.addEventListener('message', handleResponse);
    window.postMessage({ type: 'MP_EXT_SEARCH', messageId, params }, '*');
  });
}

/**
 * Fetch all logs stored by the extension's service worker.
 * Returns an array of timestamped strings.
 */
export function getExtensionLogs() {
  return new Promise((resolve) => {
    const messageId = `log_${Date.now()}`;
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve([]);
    }, 5000);

    const handler = (event) => {
      if (
        event.data &&
        event.data.source === 'mp-ext' &&
        event.data.type === 'MP_EXT_LOGS' &&
        event.data.messageId === messageId
      ) {
        clearTimeout(timeout);
        window.removeEventListener('message', handler);
        resolve(event.data.logs || []);
      }
    };

    window.addEventListener('message', handler);
    window.postMessage({ type: 'MP_EXT_GET_LOGS', messageId }, '*');
  });
}

/**
 * Download extension logs as a .txt file.
 */
export async function downloadExtensionLogs() {
  const logs = await getExtensionLogs();
  const text = logs.length ? logs.join('\n') : '(sin logs)';
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `mp-ext-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Clear all logs stored in the extension.
 */
export function clearExtensionLogs() {
  const messageId = `clr_${Date.now()}`;
  window.postMessage({ type: 'MP_EXT_CLEAR_LOGS', messageId }, '*');
}
